import { useApiQuery } from '../useApiQuery';
import { apiGet, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';

export interface JobFinancialSummary {
  lifetimeValue: number;
  /**
   * The job has an invoice that is not VOID (drafts included): with a price
   * above zero, what holds the job's payments to `remaining` on the server.
   * Absent from an older server.
   */
  hasInvoice?: boolean;
  /** Settled money toward the job. */
  paid: number;
  /** Everything paid toward the job, settled or clearing: `paid + clearing`. */
  collected?: number;
  /** The issued invoices' balances, as the books hold them. */
  outstanding: number;
  /**
   * `outstanding` less the bank debits clearing on each invoice, which
   * `collected` counts. Sent with `collected`; absent from an older server.
   */
  outstandingNet?: number;
  /** Bank debits accepted but not yet settled; not part of `paid`. */
  clearing?: number;
  /**
   * What is still owed once every payment made is counted, clearing ones
   * included: open invoices' balances (tax included) less the debits clearing
   * on them, plus any uninvoiced job total less job money not yet applied.
   */
  remaining?: number;
  /** Settled money toward the job deposit, which may arrive in parts. */
  depositPaid?: number;
  /** Deposit bank debits still clearing; not part of `depositPaid`. */
  depositClearing?: number;
  scheduledRevenue: number;
  nextInvoice: { date: string; amount: number } | null;
  currency: string;
}

/** §15 — lifetime value / paid / outstanding / scheduled revenue for a job. */
export function useJobFinancialSummary(jobId: string, enabled = true) {
  return useApiQuery<JobFinancialSummary>({
    queryKey: jobKeys.financialSummary(jobId),
    queryFn: () =>
      apiGet<JobFinancialSummary>(operationsClient, jobEndpoints.financialSummary(jobId)),
    enabled: Boolean(jobId) && enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    showErrorToast: false,
  });
}
